import { useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';

type QuantityStepperProps = {
    amount: number;
    onDecrease: () => void;
    onIncrease: () => void;
    minAmount?: number;
    maxAmount?: number;
    decreaseLabel?: string;
    increaseLabel?: string;
    /** `default` (white) sits on tinted panels like the shop toolbar; `neutral` is for white pages. */
    surface?: 'default' | 'neutral';
};

/**
 * DS quantity stepper (Figma 173:10917) — minus | value | plus on a white
 * rounded surface. Icons inherit `currentColor` from the button so colour
 * comes from the Tailwind text-* class. Clamping (`minAmount` / `maxAmount`)
 * is opt-in so consumers can keep their own clamp logic instead.
 */
export const QuantityStepper = (props: QuantityStepperProps) => {
    const { localize } = useContext(AppContext);

    const decreaseDisabled = () => props.minAmount !== undefined && props.amount <= props.minAmount;
    const increaseDisabled = () => props.maxAmount !== undefined && props.amount >= props.maxAmount;

    return (
        <div class={clsx(
            'inline-flex items-center rounded-cvi-md',
            props.surface === 'neutral' ? 'bg-cvi-bg-neutral-subtle' : 'bg-cvi-bg-default',
        )}>
            <button
                type="button"
                class="flex items-center justify-center px-cvi-5 py-cvi-5 text-cvi-fg-link cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={props.onDecrease}
                disabled={decreaseDisabled()}
                aria-label={props.decreaseLabel || localize('decrease-quantity', 'Decrease quantity')}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M5 11h14a1 1 0 1 1 0 2H5a1 1 0 1 1 0-2z" />
                </svg>
            </button>
            <span class="body-md text-cvi-fg-default text-center w-cvi-9" aria-live="polite">
                {props.amount}
            </span>
            <button
                type="button"
                class="flex items-center justify-center px-cvi-5 py-cvi-5 text-cvi-fg-link cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={props.onIncrease}
                disabled={increaseDisabled()}
                aria-label={props.increaseLabel || localize('increase-quantity', 'Increase quantity')}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M13 5a1 1 0 1 0-2 0v6H5a1 1 0 1 0 0 2h6v6a1 1 0 1 0 2 0v-6h6a1 1 0 1 0 0-2h-6V5z" />
                </svg>
            </button>
        </div>
    );
};
